/**
 * "AI suggestions" ("KI-Vorschläge"): docked generically into the
 * shared building blocks a form dialog or a detail page already renders
 * (`commons/layout/PageHeader.tsx`'s `actionMenu` area; every `@msys/ui`
 * `Modal` header via the `ModalHeaderExtrasProvider` mounted in
 * `main-routes/Routes.tsx`), so it appears almost everywhere without
 * per-screen work. A click opens the assistant panel
 * (`useAgentPanelControl().ask`) in a NEW conversation (never continuing
 * whichever one is open) and asks what the assistant can suggest for
 * whatever the user is doing, with a hidden machine block carrying the
 * dialog/page title, the route and the current — possibly unsaved — form
 * values. Values come from the dialog's own `<AgentDialogFormValues />`
 * reporters (`agentDialogFormValues.tsx`, works where `<Formik>` sits inside
 * the modal content), falling back to an enclosing Formik context (where
 * `<Formik>` wraps the `Modal`/page); with neither, no values are sent.
 *
 * `ModalHeaderExtrasProvider` renders one static `extras` element for every
 * modal, so a button mounted that way never receives that modal's own
 * `title` prop — it falls back to the current page's title (`Page.tsx`'s
 * `Helmet`-set `document.title`) when none is passed explicitly.
 *
 * Hidden outside the assistant's availability rule (`useAgentAvailable`).
 */
import { AutoAwesome as AutoAwesomeIcon } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { FormikContext } from "formik";
import React from "react";
import { useLocation } from "react-router-dom";
import {
  buildAgentContextMessage,
  type AgentContextKind,
} from "./agentContextSpec.js";
import { useDialogFormValues } from "./agentDialogFormValues.js";
import { useAgentPanelControl } from "./agentPanelControl.js";
import { useAgentAvailable } from "./useAgentAvailable.js";

/** `Page.tsx`'s `Helmet` title template — stripped off to recover the plain page title. */
const DOCUMENT_TITLE_PREFIX = "MeisterSystems.de - ";

function derivePageTitleFallback(): string | undefined {
  return document.title.startsWith(DOCUMENT_TITLE_PREFIX)
    ? document.title.slice(DOCUMENT_TITLE_PREFIX.length)
    : undefined;
}

export function AgentAssistButton({
  kind,
  title,
}: {
  /** Whether this docks a form dialog or a detail page — carried in the hidden block. */
  kind: AgentContextKind;
  /** The dialog's/page's title, when known as plain text. Falls back to the current page title when omitted. */
  title?: string;
}) {
  const { t } = useTranslate(["Assistant"]);
  const isAvailable = useAgentAvailable();
  const { ask } = useAgentPanelControl();
  const location = useLocation();
  // Not `useFormikContext()`: that throws outside a `<Formik>` provider, and
  // most pages (and some dialogs) render this button with none — read the
  // context directly and tolerate `undefined`.
  const formik = React.useContext(FormikContext);

  const readDialogValues = useDialogFormValues();
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  if (!isAvailable) return null;

  const label = t("AI suggestions");
  const resolvedTitle = title ?? derivePageTitleFallback() ?? "";

  const handleClick = () => {
    ask(
      buildAgentContextMessage({
        kind,
        title: resolvedTitle,
        path: location.pathname,
        values:
          readDialogValues(buttonRef.current) ??
          (formik?.values as Record<string, unknown> | undefined),
        text: resolvedTitle
          ? t("What can you suggest here? {title}", {
              ns: "Assistant",
              title: resolvedTitle,
            })
          : t("What can you suggest here?"),
      }),
      // A question about this dialog/page, not a follow-up to the open chat.
      { newConversation: true }
    );
  };

  return (
    <Tooltip title={label}>
      <IconButton
        ref={buttonRef}
        size="small"
        aria-label={label}
        onClick={handleClick}
      >
        <AutoAwesomeIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
